import React, { useCallback, useEffect, useState } from 'react';
import {
	EntityId,
	EntityType,
	FunctionCallState,
} from '@elyx-code/project-logic-tree';
import { useParams } from 'react-router-dom';
import { IDialogProps } from '../../../lib/dialog/Dialog';
import { EditorService } from '../../../services/editor';
import FunctionCall from './FunctionCall';
import DialogLoader from '../../DialogLoader';
import { popupNotification } from '../../../popup-notification';
import EntityDialogHeader from '../../EntityDialogHeader';

export interface IFunctionCallDialogContentProps extends IDialogProps {
	project: EditorService;
	ready: boolean;
}

export interface IFunctionCallSectionProps {
	entity: FunctionCallState;
	project: EditorService;
}

const FunctionCallDialogContent: React.FC<IFunctionCallDialogContentProps> = ({
	onClose,
	project,
	ready,
}) => {
	const { entityId } = useParams<{
		entityType: EntityType;
		entityId: EntityId;
	}>();

	const entity: FunctionCallState | null =
		(project?.logic?.get(entityId) as FunctionCallState | null) || null;

	const onEscapePressed = useCallback(
		(e: KeyboardEvent) => {
			// Or close the dialog if there is only one tab
			onClose(e);
		},
		[onClose]
	);

	const useForceUpdate = () => {
		const [_, setValue] = useState(0);
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	useEffect(() => {
		project?.on('escape-key-press', onEscapePressed);

		if (!!entity) {
			project?.on(entity.id, () => {
				forceUpdate();
			});
		}

		return () => {
			project?.off('escape-key-press', onEscapePressed);
		};
	}, [project, entity, onEscapePressed]);

	if (!project || !entity) {
		if (!!project && project.hasLoadedLogic && !entity) {
			popupNotification({
				color: 'error',
				text: 'This trigger execution does not exist in the current version of the project',
			});
			onClose(null);
			return;
		}

		return (
			<DialogLoader
				title='Custom trigger execution node'
				subtitle='Execution builder'
				onClose={onClose}
			/>
		);
	}

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				overflow: 'hidden',
				paddingBottom: 20,
			}}
		>
			<EntityDialogHeader entity={entity} project={project} onClose={onClose} />

			<div
				style={{
					display: 'flex',
					flexGrow: 1,
					overflowY: 'scroll',
				}}
			>
				{/* We need the 'span' for the react ref */}
				<span
					style={{
						maxHeight: '100%',
						maxWidth: '100%',
						flexGrow: 1,
						height: '100%',
					}}
				>
					<FunctionCall
						entity={entity}
						project={project}
						onClose={(e) => {
							onClose(e);
						}}
					/>
				</span>
			</div>
		</div>
	);
};

export default FunctionCallDialogContent;
